<template>
  <v-dialog v-model="dialog" persistent width="720px">
    <template #activator="{ on }">
      <app-icon :data-e2e-id="`edit-${trialId}`" v-on="on" @click="onActivator">edit</app-icon>
    </template>
    <transition name="slide-x-transition" mode="out-in">
      <v-card v-if="!showTrialTerminationConfirmation" flat class="pa-3">
        <v-card-title>
          <h5>Edit Trial</h5>
          <v-spacer />
          <h5>
            <a :href="trialLink" target="_blank" class="viper-link">{{ trialExternalId }}</a>
          </h5>
        </v-card-title>
        <v-card-text>
          <v-text-field
            v-if="shouldAllowEditParentId && isComplexTrial"
            v-model.lazy.trim="form.parentOrgTrialExternalId"
            label="Parent Trial Name (**Will change all instances**)"
            :placeholder="form.parentOrgTrialExternalId"
            clearable
          />
          <v-text-field
            v-model.lazy.trim="form.friendlyName"
            data-e2e-id="edit-trial-trial-identifier"
            :label="externalIdInputLabel"
            :placeholder="friendlyName"
            :error-messages="friendlyNameErrors"
            clearable
          />
          <div v-if="nameChangeAlert" class="error--text pb-4">
            This identifier will be displayed to all users within the org.
          </div>
          <v-textarea
            v-if="editDescription"
            v-model.lazy.trim="form.description"
            :label="descriptionInputLabel"
            data-e2e-id="edit-trial-trial-description"
            clearable
          />
          <div v-if="descriptionChangeAlert" class="error--text pb-4">
            This note will be displayed to all users within the trial view.
          </div>
          <organization-trial-status-select
            v-if="trialType === 'TRIAL'"
            :value="form.status"
            @input="onTrialStatusSelect($event)"
          />
          <app-date-picker
            v-if="trialType === 'TRIAL'"
            :model="form.recruitingStartDate"
            :schema="{ label: 'Recruiting Start Date' }"
            :persistent-hint="false"
            return-object
            @change="onDateChange('recruitingStartDate', $event)"
          />
          <div v-if="recruitingStartDateChangeAlert" class="error--text pb-4">
            This change will result in a target duration of
            {{ formatDuration(potentialSummary && potentialSummary.targetDuration) | orDefault }}
          </div>
          <app-date-picker
            :model="form.recruitingEndDate"
            :schema="{ label: 'Recruiting End Date' }"
            :persistent-hint="false"
            return-object
            @change="onDateChange('recruitingEndDate', $event)"
          />
          <div v-if="recruitingEndDateChangeAlert" class="error--text pb-4">
            This change will result in a target duration of
            {{ formatDuration(potentialSummary && potentialSummary.targetDuration) | orDefault }}
            <span v-if="trialType === 'PARENT'">
              All practices within this trial will inherit this recruiting end date.
            </span>
          </div>
          <v-text-field
            v-if="editTargetEnrollment"
            v-model.lazy.trim="form.siteGoal"
            label="Target Enrollment"
            type="number"
            :min="0"
            clearable
          />
          <div v-if="siteGoalChangeAlert" class="error--text pb-4">
            This change will result in a target monthly enrollment rate of
            {{
              formatEnrollment(potentialSummary && potentialSummary.targetMonthlyEnrollment)
                | orDefault
            }}
          </div>
          <v-autocomplete
            v-if="trialType === 'TRIAL'"
            v-model="careTeamPIs"
            :items="potentialPIs"
            :item-text="getName"
            clearable
            label="Principal Investigator"
            multiple
            return-object
          />
          <v-autocomplete
            v-if="trialType === 'TRIAL'"
            v-model="careTeamCRCs"
            :items="potentialCRCs"
            :item-text="getName"
            clearable
            label="Clinical Research Coordinator(s)"
            multiple
            return-object
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <app-button-cancel @click="onCancel" />
          <app-button data-e2e-id="edit-trial-save" :request="onSave" :disabled="formErrors.friendlyNameAlreadyExists">Save</app-button>
        </v-card-actions>
      </v-card>
      <trial-completion-confirmation
        v-else
        :status="form.status"
        :contracted="trialContracted"
        @on-save="onSave"
        @on-cancel="showTrialTerminationConfirmation = false"
      />
    </transition>
  </v-dialog>
</template>

<script>
import _ from 'lodash'
import moment from 'moment'
import organizationTrialService from '@/services/organization-trial-service'
import { maxLength } from 'vuelidate/lib/validators'
import { validationMixin } from 'vuelidate'
import trialSummaryService from '@/services/trial-summary-service'
import { durationMixin } from '@/mixins/duration'
import { ENROLLMENT_PRECISION } from '@/helpers/table-helper'
import { summaryValues } from '@/helpers/summary-value-helper'
import { careTeamMemberService } from '@/services/care-team-member-service'
import { memberTypes } from '@/helpers/care-team-helper'
import { fullName } from '@/helpers/name-helper'
import organizationTrialStatusService from '@/services/organization-trial-status-service'
import OrganizationTrialStatusSelect from '@/components/insights/OrganizationTrialStatusSelect'
import TrialCompletionConfirmation from '@/components/trials/TrialCompletionConfirmation'
import { TrialStatus } from '@/helpers/trial-helper'

export default {
  components: {
    TrialCompletionConfirmation,
    OrganizationTrialStatusSelect
  },
  mixins: [durationMixin, validationMixin],
  props: {
    organizationId: { type: String, required: true },
    trialId: { type: String, required: true },
    trialExternalId: { type: String, required: true },
    friendlyName: { type: String, required: true },
    recruitingStartDate: { type: String, default: null },
    recruitingEndDate: { type: String, default: null },
    targetEnrollment: { type: Number, default: null },
    editTargetEnrollment: { type: Boolean, default: false },
    orgTrialExternalId: { type: String, default: null },
    trialLink: { type: String, default: null },
    currentCareTeam: { type: Array, default: null },
    orgTrialId: { type: String, default: '' },
    potentialCareTeamMembers: { type: Array, default: () => [] },
    orgTrialDescription: { type: String, default: null },
    editDescription: { type: Boolean, default: false },
    status: { type: String, default: null },
    orgTrial: { type: Object, default: () => {} },
    trialContracted: { type: Boolean, default: false },
    trialType: { type: String, default: 'TRIAL' },
    parentOrgTrialExternalId: { type: String, default: null },
    parentTrialExternalId: { type: String, default: null },
    shouldAllowEditParentId: { type: Boolean, default: false }
  },
  data() {
    return {
      dialog: false,
      formErrors: {
        friendlyNameAlreadyExists: false
      },
      form: {
        recruitingStartDate: this.recruitingStartDate,
        recruitingEndDate: this.recruitingEndDate,
        siteGoal: this.targetEnrollment,
        externalId: this.orgTrialExternalId,
        description: this.orgTrialDescription,
        updateDescription: this.editDescription,
        status: this.status,
        friendlyName: this.friendlyName || this.orgTrialExternalId || this.trialExternalId,
        parentOrgTrialExternalId: this.parentOrgTrialExternalId || this.parentTrialExternalId
      },
      potentialSummary: null,
      showTrialTerminationConfirmation: false,
      careTeamPIs: this.getCurrentCareTeamUsers(memberTypes.PI),
      careTeamCRCs: this.getCurrentCareTeamUsers(memberTypes.CRC)
    }
  },
  computed: {
    isComplexTrial() {
      return !!this.parentTrialExternalId || !!this.parentOrgTrialExternalId
    },
    changedFormFields() {
      const changedFields = []
      if (this.recruitingStartDateChangeAlert) {
        changedFields.push('Recruiting Start Date')
      }
      if (this.recruitingEndDateChangeAlert) {
        changedFields.push('Recruiting End Date')
      }
      if (this.siteGoalChangeAlert) {
        changedFields.push('Expected Enrollment')
      }
      if (this.nameChangeAlert) {
        changedFields.push('Trial Identifier')
      }
      return changedFields
    },
    nameChangeAlert() {
      return (
        this.form.externalId !== this.orgTrialExternalId &&
        _.isEmpty(this.form.externalId) !== _.isEmpty(this.orgTrialExternalId)
      )
    },
    descriptionChangeAlert() {
      return this.dialog && this.form.description !== this.orgTrialDescription
    },
    recruitingStartDateChangeAlert() {
      return this.formatDate(this.form.recruitingStartDate) !== this.recruitingStartDate
    },
    recruitingEndDateChangeAlert() {
      return this.formatDate(this.form.recruitingEndDate) !== this.recruitingEndDate
    },
    siteGoalChangeAlert() {
      const siteGoalString = this.form.siteGoal ? this.form.siteGoal.toString() : null
      const targetEnrollmentString = this.targetEnrollment ? this.targetEnrollment.toString() : null
      return siteGoalString !== targetEnrollmentString
    },
    formFieldsSnackbarConfigs() {
      return _.map(this.changedFormFields, fieldName => this.buildSnackbarConfig(fieldName))
    },
    friendlyNameErrors() {
      const errors = []
      this.formErrors.friendlyNameAlreadyExists && errors.push(`${this.form.friendlyName} is already taken.`)
      if (!this.$v.form.externalId.$dirty) return errors
      !this.$v.form.externalId.maxLength && errors.push('Must be 30 or fewer characters')
      return errors
    },
    potentialOrgTrialSummaryParams() {
      return {
        recruitingStartDate: this.form.recruitingStartDate,
        recruitingEndDate: this.form.recruitingEndDate,
        siteGoal: this.form.siteGoal,
        summaryValues: [summaryValues.TARGET_DURATION, summaryValues.TARGET_MONTHLY_ENROLLMENT]
      }
    },
    sortedAndMergedPotentialCareTeamMembers() {
      let currentCareTeamUsers = this.getCurrentCareTeamUsers(memberTypes)
      let existingAndPotential = _.union(this.potentialCareTeamMembers, currentCareTeamUsers)
      return _.sortBy(existingAndPotential, 'displayName')
    },
    potentialPIs() {
      return _.filter(this.sortedAndMergedPotentialCareTeamMembers, user => {
        let nonPICareTeamUserIds = this.getCareTeamUsernamesExcludingMemberType(memberTypes.PI)
        return !_.includes(nonPICareTeamUserIds, user.username)
      })
    },
    potentialCRCs() {
      return _.filter(this.sortedAndMergedPotentialCareTeamMembers, user => {
        let nonCRCCareTeamUserIds = this.getCareTeamUsernamesExcludingMemberType(memberTypes.CRC)
        return !_.includes(nonCRCCareTeamUserIds, user.username)
      })
    },
    careTeam() {
      let PIs = _.map(this.careTeamPIs, user => {
        return this.getCareTeamMemberRequest(memberTypes.PI, user.username)
      })
      let CRCs = _.map(this.careTeamCRCs, user => {
        return this.getCareTeamMemberRequest(memberTypes.CRC, user.username)
      })
      let others = _.map(
        _.filter(this.currentCareTeam, careTeamMember => {
          return (
            careTeamMember.memberType !== memberTypes.PI &&
            careTeamMember.memberType !== memberTypes.CRC
          )
        }),
        careTeamMember => {
          return this.getCareTeamMemberRequest(
            careTeamMember.memberType,
            careTeamMember.user.username
          )
        }
      )
      return _.concat(PIs, CRCs, others)
    },
    careTeamMembersToCreate() {
      return _.filter(this.careTeam, careTeamMember => {
        return !_.find(this.currentCareTeam, currentCareTeamMember => {
          return (
            currentCareTeamMember.memberType === careTeamMember.memberType &&
            currentCareTeamMember.user.username === careTeamMember.username
          )
        })
      })
    },
    careTeamMembersToDelete() {
      return _.map(
        _.filter(this.currentCareTeam, careTeamMember => {
          return !_.find(this.careTeam, it => it.username === careTeamMember.user.username)
        }),
        careTeamMember => careTeamMember.id
      )
    },
    externalIdInputLabel() {
      return _.get(this.orgTrial, 'trial.overallParentTrial')
        ? 'Cohort Identifier'
        : 'Trial Identifier'
    },
    descriptionInputLabel() {
      return _.get(this.orgTrial, 'trial.overallParentTrial')
        ? 'Cohort Description'
        : 'Trial Description'
    },
    hasTerminatedOrCompletedTrial() {
      return (
        this.form.status !== this.status &&
        _.includes([TrialStatus.TERMINATED.value, TrialStatus.COMPLETED.value], this.form.status)
      )
    }
  },
  watch: {
    async potentialOrgTrialSummaryParams() {
      if (
        this.recruitingStartDateChangeAlert ||
        this.recruitingEndDateChangeAlert ||
        this.siteGoalChangeAlert
      ) {
        if (this.trialType === 'TRIAL') {
          const { data } = await trialSummaryService.potentialOrganizationTrialSummary(
            this.trialId,
            this.potentialOrgTrialSummaryParams
          )
          this.potentialSummary = data
        }
        if (this.trialType === 'PARENT') {
          const { data } = await trialSummaryService.potentialParentTrialSummary(
            this.trialId,
            this.potentialOrgTrialSummaryParams
          )
          this.potentialSummary = data
        }
      } else {
        this.potentialSummary = null
      }
    },
    'form.friendlyName': _.debounce(async function() {
      const response = await organizationTrialService.existsByOrganizationAndExternalId(this.organizationId, this.trialId, this.form.friendlyName)
      if (response.status === 200) {
        this.formErrors.friendlyNameAlreadyExists = response.data
      }
    }, 200)
  },
  methods: {
    onActivator() {
      this.form = {
        recruitingStartDate: this.recruitingStartDate,
        recruitingEndDate: this.recruitingEndDate,
        siteGoal: this.targetEnrollment,
        externalId: this.orgTrialExternalId,
        description: this.orgTrialDescription,
        updateDescription: this.editDescription,
        status: this.status,
        friendlyName: this.friendlyName,
        parentOrgTrialExternalId: this.parentOrgTrialExternalId || this.parentTrialExternalId
      }
      this.careTeamPIs = this.getCurrentCareTeamUsers(memberTypes.PI)
      this.careTeamCRCs = this.getCurrentCareTeamUsers(memberTypes.CRC)
      this.$emit('click', this.trialId)
    },
    onCancel() {
      this.form = {
        recruitingStartDate: this.recruitingStartDate,
        recruitingEndDate: this.recruitingEndDate,
        siteGoal: this.targetEnrollment,
        externalId: this.orgTrialExternalId,
        status: this.status
      }
      this.careTeamPIs = this.getCurrentCareTeamUsers(memberTypes.PI)
      this.careTeamCRCs = this.getCurrentCareTeamUsers(memberTypes.CRC)
      this.showTrialTerminationConfirmation = false
      this.dialog = false
    },
    async onSave() {
      if (this.hasTerminatedOrCompletedTrial && !this.showTrialTerminationConfirmation) {
        this.showTrialTerminationConfirmation = true
        return
      }
      if (this.trialType === 'TRIAL') {
        await this.deleteCareTeamMembers(this.careTeamMembersToDelete)
        await this.createCareTeamMembers(this.careTeamMembersToCreate)

        const toSubmit = _.merge({}, this.form, { externalId: this.form.friendlyName })
        await organizationTrialService.update(this.organizationId, this.trialId, toSubmit)
        if (this.status !== this.form.status) {
          await organizationTrialStatusService.create({
            organizationTrialId: this.orgTrialId,
            value: this.form.status
          })
          if (
            this.form.status === TrialStatus.COMPLETED.value ||
            this.form.status === TrialStatus.TERMINATED.value
          ) {
            this.$store.dispatch('GET_TRIAL_NOTIFICATION_PREFERENCES')
          }
        }
      } else if (this.trialType === 'PARENT') {
        await organizationTrialService.parentUpdate(
          this.organizationId,
          this.trialId,
          {
            externalId: this.form.friendlyName,
            primaryCompletionDate: this.form.recruitingEndDate
          }
        )
      }
      this.$emit(
        'edit-site-trial',
        _.merge({}, { organizationId: this.organizationId, trialId: this.trialId }, this.form)
      )
      this.$store.dispatch('ADD_SNACKBAR_CONFIG', this.formFieldsSnackbarConfigs)
      this.$store.dispatch('REFRESH_TRIALS')
      this.onCancel()
    },
    onDateChange(fieldName, event) {
      if (event.valid) {
        this.form[fieldName] = this.formatDate(event.value)
      }
      event.valid ? (this.form[fieldName] = event.value) : (this.form[fieldName] = null)
    },
    formatDate(date) {
      return date ? moment(date).format('YYYY-MM-DD') : date
    },
    formatEnrollment(number) {
      return _.isNil(number) ? number : number.toFixed(ENROLLMENT_PRECISION)
    },
    buildSnackbarConfig(field) {
      return {
        message: `Your ${field} has been changed.`
      }
    },
    getCareTeamUsernamesExcludingMemberType(memberType) {
      return _.map(
        _.filter(this.careTeam, careTeamMember => {
          return careTeamMember.memberType !== memberType
        }),
        careTeamMember => {
          return careTeamMember.username
        }
      )
    },
    getCurrentCareTeamUsers(memberTypes) {
      return _.map(
        _.filter(this.currentCareTeam, careTeamMember => {
          return _.includes(memberTypes, careTeamMember.memberType)
        }),
        careTeamMember => {
          return _.merge({}, careTeamMember.user, { displayName: fullName(careTeamMember.user) })
        }
      )
    },
    getCareTeamMemberRequest(memberType, username) {
      return {
        memberType: memberType,
        username: username,
        orgTrialId: this.orgTrialId
      }
    },
    async createCareTeamMembers(careTeamMembers) {
      _.forEach(careTeamMembers, async careTeamMember => {
        await careTeamMemberService.create(this.organizationId, careTeamMember)
      })
    },
    async deleteCareTeamMembers(careTeamMemberIds) {
      _.forEach(careTeamMemberIds, async careTeamMemberId => {
        await careTeamMemberService.delete(this.organizationId, this.orgTrialId, careTeamMemberId)
      })
    },
    getName(user) {
      return `${user.displayName} ${user.email}`
    },
    onTrialStatusSelect(status) {
      this.form.status = status
      if (status === TrialStatus.RECRUITING.value && _.isNil(this.form.recruitingStartDate)) {
        this.form.recruitingStartDate = this.formatDate(moment())
      } else if (
        (status === TrialStatus.TERMINATED.value || status === TrialStatus.COMPLETED.value) &&
        _.isNil(this.form.recruitingEndDate)
      ) {
        this.form.recruitingEndDate = this.formatDate(moment())
      }
    }
  },
  validations: {
    form: {
      externalId: {
        maxLength: maxLength(30)
      }
    }
  }
}
</script>
